<template>
  <div>
    <a-article
      :article-data="articleData"
      :related-articles="relatedArticles"
      :related-videos="relatedVideos"
      :article-slug="articleSlug"
      :category-slug="categorySlug"
      :subcategory-slug="subcategorySlug"
      :is-ebook-loaded="isEbookLoaded"
      :show-disclaimer="isDisclaimerVisible"
      @get-ebook-data="getEbookData"
    />
  </div>
</template>

<script>
import mixins from '@/utils/mixins'
import AArticle from '@/components/_sections/article/AArticle'
import { SPECIAL_SUBCATEGORY } from 'enums/special-categories'
import { checkIfNonCanonicalPath } from '@/utils/helpers/canonical-links'
import { EBOOK_LOCATION } from 'shared/AEbook/enums'
import {
  METHOD_NAME_TO_RUN_ON_LOGIN,
  METHOD_NAME_TO_RUN_ON_LOGOUT
} from '@/utils/mixins/run-method-on-login-logout'
import { METHOD_NAME, PENDING_ACTION } from 'enums/pending-actions'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import { pick } from 'ramda'
import { RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME } from 'enums/article'

async function getArticlePageData({ store, route }) {
  const { category, article } = route.params
  const { previewId } = route.query
  const { dispatch } = store

  const payload = {
    articleSlug: article,
    previewId
  }

  const { articleData, relatedArticles, relatedVideos } = await dispatch(
    'articles/requestArticlePageData',
    payload
  )

  const ebookFetchProps = {
    location: EBOOK_LOCATION.ARTICLE,
    requestPayload: payload
  }

  return {
    articleData,
    relatedArticles,
    relatedVideos,
    articleSlug: article,
    categorySlug: category,
    ebookFetchProps
  }
}

function generateDefaultArticleContentDataModel() {
  return {
    articleData: null,
    relatedArticles: null,
    relatedVideos: []
  }
}

export default {
  name: 'EducationArticlePage',
  mixins: [
    mixins.ebookDataFetch,
    mixins.runMethodOnLoginLogout,
    mixins.refreshDataHandler,
    mixins.refreshDataListenersFactory({
      refreshHandlerByType: {
        [PENDING_ACTION.SAVE_ARTICLE]: METHOD_NAME.UPDATE_IS_SAVED_VALUE,
        [PENDING_ACTION.FOLLOW_TAG]: METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE,
        [PENDING_ACTION.FOLLOW_AUTHOR]:
          METHOD_NAME.UPDATE_IS_FOLLOWED_AUTHOR_VALUE
      }
    }),
    mixins.delayedContentRefresh
  ],
  components: { AArticle },
  async asyncData(ctx) {
    const { path: currentPath, fullPath } = ctx.route
    const { previewId } = ctx.route.query

    try {
      const pageData = await getArticlePageData({
        store: ctx.store,
        route: ctx.route
      })

      if (
        !previewId &&
        checkIfNonCanonicalPath(ctx, {
          currentPath,
          canonicalUrl: pageData.articleData.canonicalUrl
        })
      ) {
        redirectHandler({ ctx, to: pageData.articleData.canonicalUrl })
        return
      }

      return pageData
    } catch (err) {
      ctx.app.$errorHandler(err, ctx, {
        showErrorPage: true,
        componentName: 'EducationArticlePage'
      })
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  data() {
    return {
      ...generateDefaultArticleContentDataModel(),
      articleSlug: null,
      categorySlug: null,
      subcategorySlug: SPECIAL_SUBCATEGORY.EDUCATION_CENTER,
      isEbookLoaded: false
    }
  },
  computed: {
    isDisclaimerVisible() {
      return !!this.articleData?.isDisclaimerVisible
    }
  },
  methods: {
    async getEbookData() {
      const { location, requestPayload } = this.ebookFetchProps || {}

      this.articleData.Ebook = await this.$_ebookDataFetch_getEbook(
        location,
        requestPayload
      )

      this.isEbookLoaded = true
    },
    [METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE]({ id, value }) {
      if (this.articleData.mainTag.id !== id) return

      this.articleData.mainTag = {
        ...this.articleData.mainTag,
        isFollowed: value
      }
      this.articleData.tags = this.articleData.tags.map(tagData => {
        if (tagData.id === id) return this.articleData.mainTag

        return tagData
      })
    },
    [METHOD_NAME.UPDATE_IS_FOLLOWED_AUTHOR_VALUE]({ id, value }) {
      this.articleData.publishers = this.articleData.publishers.map(
        publisher => {
          if (publisher.id === id) {
            return {
              ...publisher,
              isFollowed: value
            }
          }

          return publisher
        }
      )
    },
    [METHOD_NAME.UPDATE_IS_SAVED_VALUE]({ value }) {
      this.articleData.isSaved = value
    },
    [METHOD_NAME_TO_RUN_ON_LOGOUT]() {
      this.articleData.isSaved = false
      this.articleData.tags = this.articleData.tags.map(tag => ({
        ...tag,
        isFollowed: false
      }))
      this.articleData.publishers = this.articleData.publishers.map(
        publisher => ({ ...publisher, isFollowed: false })
      )
    },
    async [METHOD_NAME_TO_RUN_ON_LOGIN]() {
      const payload = {
        articleSlug: this.$helper.getLastPathParam(this.$route.path)
      }

      const {
        isSaved = this.articleData.isSaved,
        authorsIsFollowed = this.articleData.publishers.map(
          ({ isFollowed }) => isFollowed
        ),
        tags = this.articleData.tags,
        mainTag = this.articleData.mainTag
      } = await this.$_refreshDataHandler_getSingleArticleEntities(payload)

      this.articleData = {
        ...this.articleData,
        isSaved,
        publishers: this.articleData.publishers.map((publisher, index) => ({
          ...publisher,
          isFollowed: authorsIsFollowed[index]
        })),
        tags,
        mainTag
      }
    }
  },
  mounted() {
    const dataKeysForDelayedContentRefresh = Object.keys(
      generateDefaultArticleContentDataModel()
    )

    this.$_delayedContentRefresh_run({
      getOldDataFn: () => pick(dataKeysForDelayedContentRefresh, this),
      requestNewDataFn: async () => {
        const newData = await getArticlePageData({
          route: this.$route,
          store: this.$store
        })

        return pick(dataKeysForDelayedContentRefresh, newData)
      },
      dataComparisonKeysToOmitDeep: ['ModifiedOn'],
      dataChangedCallbackFn: newData => {
        Object.assign(this, pick(dataKeysForDelayedContentRefresh, newData))
        this.$bus.$emit(RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME)
      }
    })
  }
}
</script>
